(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let giftBusy = false;

  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const noDecimals = Math.round(cents / 100).toString();
    const match = format && format.match(/\{\{\s*(\w+)\s*\}\}/);
    let amount = value;

    if (match) {
      switch (match[1]) {
        case 'amount_no_decimals':
          amount = noDecimals;
          break;
        case 'amount_with_comma_separator':
          amount = value.replace('.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          amount = noDecimals;
          break;
        default:
          amount = value;
      }
    }

    return format ? format.replace(/\{\{\s*\w+\s*\}\}/g, amount) : amount;
  };

  class CartProgressBar extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-cart-progress-message]');
      this.shippingCents = parseInt(this.dataset.shippingCents, 10) || 0;
      this.giftCents = parseInt(this.dataset.giftCents, 10) || 0;
      this.enableGift = this.dataset.enableGift === 'true';
      this.giftVariantId = this.dataset.giftVariantId || '';
      this.format = this.dataset.currencyFormat || '';
      this.messages = {
        below: this.dataset.msgBelow || '',
        between: this.dataset.msgBetween || '',
        complete: this.dataset.msgComplete || '',
      };
      const seededMax = parseInt(this.dataset.maxCents, 10);
      this.maxCents = Number.isNaN(seededMax)
        ? (this.enableGift && this.giftCents > this.shippingCents ? this.giftCents : this.shippingCents)
        : seededMax;

      const seeded = parseInt(this.dataset.cartTotal, 10);
      this.lastQualifying = Number.isNaN(seeded) ? null : seeded;

      this.drawer = this.closest('cart-drawer');
      this.sectionId = this.drawer && this.drawer.dataset.sectionId;

      this.countEl = this.drawer && this.drawer.querySelector('#NewCartCount');
      if (this.countEl) {
        this.observer = new MutationObserver(() => this.scheduleRefresh());
        this.startObserving();
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.timer);
    }

    startObserving() {
      if (this.observer && this.countEl) {
        this.observer.observe(this.countEl, { childList: true, subtree: true, characterData: true });
      }
    }

    scheduleRefresh() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 150);
    }

    findGiftLine(cart) {
      return cart.items.find((item) => item.properties && item.properties._free_gift) || null;
    }

    async refresh() {
      if (this.busy) return;
      try {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        const giftLine = this.findGiftLine(cart);
        const qualifying = cart.total_price - (giftLine ? giftLine.final_line_price : 0);

        if (qualifying !== this.lastQualifying) {
          this.lastQualifying = qualifying;
          this.update(qualifying);
        }

        await this.reconcileGift(qualifying, giftLine);
      } catch (error) {
      }
    }

    captureSecondary() {
      if (this.rate != null || !this.messageEl) return;
      const current = this.messageEl.textContent;
      const secondary = current.match(/\(([\d.,]+)\s*([^\d().,\s]+)\s*\)/);
      const primary = current.match(/([\d.,]+)/);
      if (!secondary || !primary) return;
      const toNum = (s) => parseFloat(s.replace(/\s/g, '').replace(/\.(?=\d{3})/g, '').replace(',', '.'));
      const primaryNum = toNum(primary[1]);
      const secondaryNum = toNum(secondary[1]);
      if (primaryNum > 0 && secondaryNum > 0) {
        this.rate = secondaryNum / primaryNum;
        this.secondaryUnit = secondary[2];
      }
    }

    formatRemaining(cents) {
      const safe = Math.max(cents, 0);
      const primary = formatMoney(safe, this.format);
      if (this.rate != null && this.secondaryUnit) {
        const secondary = ((safe / 100) * this.rate).toFixed(2).replace('.', ',');
        return `${primary} (${secondary} ${this.secondaryUnit})`;
      }
      return primary;
    }

    update(total) {
      this.captureSecondary();

      const ratio = this.maxCents > 0 ? Math.min((total / this.maxCents) * 100, 100) : 0;
      this.style.setProperty('--cart-progress-fill', `${ratio}%`);

      const shippingMilestone = this.querySelector('[data-cart-progress-milestone="shipping"]');
      const giftMilestone = this.querySelector('[data-cart-progress-milestone="gift"]');
      if (shippingMilestone) shippingMilestone.classList.toggle('is-reached', total >= this.shippingCents);
      if (giftMilestone) giftMilestone.classList.toggle('is-reached', total >= this.giftCents);

      let text;
      if (total < this.shippingCents) {
        text = this.messages.below.replace('[amount]', this.formatRemaining(this.shippingCents - total));
      } else if (this.enableGift && total < this.giftCents) {
        text = this.messages.between.replace('[amount]', this.formatRemaining(this.giftCents - total));
      } else {
        text = this.messages.complete;
      }
      if (this.messageEl && this.messageEl.textContent !== text) this.messageEl.textContent = text;
    }

    async reconcileGift(qualifying, giftLine) {
      if (window.Shopify && window.Shopify.designMode) return;
      if (!this.enableGift || !this.giftVariantId || this.giftCents <= 0) return;
      if (giftBusy) return;

      if (qualifying >= this.giftCents && !giftLine) {
        await this.mutate('cart/add.js', {
          items: [{ id: Number(this.giftVariantId), quantity: 1, properties: { _free_gift: 'true' } }],
        });
      } else if (qualifying < this.giftCents && giftLine) {
        await this.mutate('cart/change.js', { id: giftLine.key, quantity: 0 });
      }
    }

    async mutate(endpoint, body) {
      this.busy = true;
      giftBusy = true;
      if (this.observer) this.observer.disconnect();
      try {
        if (this.sectionId) body.sections = this.sectionId;
        const response = await fetch(`${root}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) return;
        const data = await response.json();
        this.applySections(data.sections);
      } catch (error) {
      } finally {
        this.busy = false;
        giftBusy = false;
        this.startObserving();
      }
    }

    applySections(sections) {
      if (!sections || !this.sectionId || !sections[this.sectionId] || !this.drawer) return;

      const parsed = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
      const template = parsed.querySelector('template[data-sidebar-template]');
      const source = template ? template.content : parsed;

      ['[data-cart-drawer-results]', '.cart-drawer-subtotal'].forEach((selector) => {
        const next = source.querySelector(selector);
        const current = this.drawer.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });
    }
  }

  if (!customElements.get('cart-progress-bar')) {
    customElements.define('cart-progress-bar', CartProgressBar);
  }
})();
